import React from 'react';
import { StatCard } from '../../shared/StatCard';
import userService from '../../../services/userService';

const UsersTab = ({ 
  userData, 
  userLoading, 
  loadUserData, 
  handleReviewUser,
  getStatusColor,
  getAccessLevelColor,
  userClaims
}) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const [updatingNotificationUserId, setUpdatingNotificationUserId] = React.useState(null);

  const toLower = (val) => (val || '').toString().toLowerCase();
  const matchesQuery = (user) => {
    const q = toLower(searchQuery);
    if (!q) return true;
    return [
      user.name,
      user.lastName,
      user.email,
      user.country,
      user.accessLevel,
      user.status
    ].some(field => toLower(field).includes(q));
  };

  const pendingFiltered = userData.pendingUsers.filter(matchesQuery);
  const usersFiltered = userData.users.filter(matchesQuery);

  const truncateName = (value) => {
    const str = (value || '').toString().trim();
    if (!str) return '';
    return str.length > 10 ? `${str.slice(0, 10)}...` : str;
  };

  const isEmailNotificationsEnabled = (user) => {
    if (typeof user?.emailNotificationsEnabled === 'boolean') {
      return user.emailNotificationsEnabled;
    }
    if (typeof user?.emailNotificationsEnabled === 'number') {
      return user.emailNotificationsEnabled === 1;
    }
    return true;
  };

  const handleToggleEmailNotifications = async (user) => {
    const requesterEmail = userClaims?.email || '';
    if (!requesterEmail) {
      alert('Could not identify admin email. Please sign in again.');
      return;
    }
    const nextValue = !isEmailNotificationsEnabled(user);
    setUpdatingNotificationUserId(user.id);
    try {
      await userService.updateUserEmailNotifications(user.id, requesterEmail, nextValue);
      await loadUserData();
    } catch (error) {
      console.error('Failed to update email notifications:', error);
      alert('Failed to update email notification preference. Please try again.');
    } finally {
      setUpdatingNotificationUserId(null);
    }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">User Management</h2>
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search users..."
            className="border border-gray-300 rounded px-3 py-2 w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button 
            onClick={loadUserData}
            disabled={userLoading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded flex items-center"
          >
            <i className={`fa-solid fa-sync-alt mr-2 ${userLoading ? 'animate-spin' : ''}`}></i>
            {userLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Total Users"
          value={userLoading ? 'Loading...' : userData.users.length}
          icon="fa-users"
          color="blue"
        />
        <StatCard
          title="Pending Approval"
          value={userLoading ? 'Loading...' : userData.pendingUsers.length}
          icon="fa-clock"
          color="yellow"
        />
        <StatCard
          title="Approved Users"
          value={userLoading ? 'Loading...' : userData.users.filter(u => u.status === 'approved').length}
          icon="fa-check-circle"
          color="green"
        />
        <StatCard
          title="Rejected Users"
          value={userLoading ? 'Loading...' : userData.users.filter(u => u.status === 'rejected').length}
          icon="fa-ban"
          color="red"
        />
      </div>

      {/* Pending Users */}
      {userData.pendingUsers.length > 0 && (
        <div className="bg-white border rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Pending Approval</h3>
          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/5">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Country</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Registration Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingFiltered.map((user) => (
                  <tr key={user.id}>
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <i className="fa-solid fa-user text-gray-600"></i>
                          </div>
                        </div>
                        <div className="ml-4 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {truncateName(user.name)} {truncateName(user.lastName)}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{user.email}</div>
                          {userService.isUnicefDomain(user.email) && (
                            <span className="mt-1 inline-flex px-2 text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              UNICEF
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900 truncate">
                      {user.country || 'Not specified'}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium">
                      <button 
                        onClick={() => handleReviewUser(user)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <i className="fa-solid fa-eye mr-1"></i>Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* All Users */}
      <div className="bg-white border rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">All Users</h3>
        <div className="overflow-x-auto">
          <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[24%]">User</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">Country</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[10%]">Access Level</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[9%]">Status</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[11%]">Last Login</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[18%]">Email Notifications</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[8%]">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {usersFiltered.map((user) => (
                <tr key={user.id}>
                  <td className="px-3 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <i className="fa-solid fa-user text-gray-600"></i>
                        </div>
                      </div>
                      <div className="ml-4 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {truncateName(user.name)} {truncateName(user.lastName)}
                        </div>
                        <div className="text-xs text-gray-500 truncate">{user.email}</div>
                        {userService.isUnicefDomain(user.email) && (
                          <span className="mt-1 inline-flex px-2 text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                            UNICEF
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900 truncate">
                    {user.country || 'Not specified'}
                  </td>
                  <td className="px-3 py-4">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getAccessLevelColor(user.accessLevel)}`}>
                      {user.accessLevel || 'None'}
                    </span>
                  </td>
                  <td className="px-3 py-4">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(user.status)}`}>
                      {user.status}
                    </span>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-900">
                    <label className="inline-flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={isEmailNotificationsEnabled(user)}
                        onChange={() => handleToggleEmailNotifications(user)}
                        disabled={Boolean(updatingNotificationUserId) || userLoading}
                      />
                      <span>
                        {isEmailNotificationsEnabled(user) ? 'Enabled' : 'Disabled'}
                        {updatingNotificationUserId === user.id && (
                          <i className="fa-solid fa-spinner fa-spin ml-2 text-gray-400"></i>
                        )}
                      </span>
                    </label>
                  </td>
                  <td className="px-3 py-4 text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleReviewUser(user)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <i className="fa-solid fa-edit"></i>
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        <i className="fa-solid fa-ban"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default UsersTab; 